import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import {
  getDailyCard,
  getStreak,
  type ReadingRecord,
  type StreakReward,
  type StreakState,
} from '@/api/client';
import { useUserStore } from '@/store/userStore';
import TarotCard from '@/components/TarotCard';
import ScratchCard from '@/components/ScratchCard';
import MoonPhase from '@/components/MoonPhase';
import OrnateFrame from '@/components/OrnateFrame';
import RichText from '@/components/RichText';
import StreakChip from '@/components/StreakChip';
import RewardModal from '@/components/RewardModal';
import WelcomeTour from '@/components/WelcomeTour';
import { useAudio } from '@/hooks/useAudio';

/**
 * Local midnight countdown — recalculated every minute.
 * Returns a localized "Hh Mm" string (or "<1 min" if very close).
 */
function useCountdownToMidnight(language: string): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  return useMemo(() => {
    const d = new Date(now);
    const tomorrow = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0);
    const ms = tomorrow.getTime() - now;
    const totalMin = Math.max(0, Math.floor(ms / 60_000));
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (totalMin < 1) return language.startsWith('ru') ? '< 1 мин' : '< 1 min';
    if (h === 0) return language.startsWith('ru') ? `${m} мин` : `${m} min`;
    return language.startsWith('ru') ? `${h} ч ${m} мин` : `${h}h ${m}m`;
  }, [now, language]);
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const audio = useAudio();
  const user = useUserStore((s) => s.user);
  const [reading, setReading] = useState<ReadingRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);

  // Streak state — fetched separately so cached daily cards still show it.
  const [streak, setStreak] = useState<StreakState | null>(null);
  // Reward queue — shown one-by-one when a milestone fires.
  const [rewardQueue, setRewardQueue] = useState<StreakReward[]>([]);

  // First-visit tour — Onboarding sets `tour_pending=1` on completion.
  // We also fall back to showing it for any logged-in user who hasn't
  // seen it yet (covers users created before this feature shipped).
  const [showTour, setShowTour] = useState(() => {
    try {
      const pending = localStorage.getItem('tour_pending') === '1';
      const seen = localStorage.getItem('tour_seen') === '1';
      return pending || !seen;
    } catch {
      return false;
    }
  });
  const closeTour = () => {
    try {
      localStorage.setItem('tour_seen', '1');
      localStorage.removeItem('tour_pending');
    } catch {}
    setShowTour(false);
  };

  const countdown = useCountdownToMidnight(i18n.language);

  // Storage key changes daily — "revealed" status resets at midnight naturally.
  const revealKey = (id: string) => `daily_revealed_${id}`;

  const drawCard = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getDailyCard();
      setReading(r);
      // If this exact card was already scratched earlier today, skip the
      // scratch step entirely — the card stays open until midnight.
      const wasRevealed = localStorage.getItem(revealKey(r.id)) === '1';
      if (wasRevealed) setRevealed(true);

      // First-visit-of-day responses carry the bumped streak. Use it
      // immediately and queue any rewards earned this tick.
      if (r.streak) {
        setStreak({
          current: r.streak.current,
          longest: r.streak.longest,
          achievements: r.streak.achievements,
          next_milestone: r.streak.next_milestone,
          next_reward: r.streak.next_reward,
        });
        if (r.streak.new_rewards?.length) {
          setRewardQueue(r.streak.new_rewards);
        }
      } else {
        // Cached daily-card responses don't include streak — fetch it.
        getStreak().then(setStreak).catch(() => {});
      }
    } catch (err) {
      setError((err as Error).message);
      // Even if daily card fails (e.g. onboarding incomplete), try to
      // load streak so a returning user still sees their progress.
      getStreak().then(setStreak).catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { drawCard().catch(() => {}); }, []);

  const dismissReward = () => {
    setRewardQueue((q) => q.slice(1));
  };

  const onReveal = () => {
    setRevealed(true);
    audio.play('bell');
    if (reading) {
      try { localStorage.setItem(revealKey(reading.id), '1'); } catch {}
    }
  };

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return t('home.greeting_morning');
    if (h < 18) return t('home.greeting_afternoon');
    return t('home.greeting_evening');
  })();

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      {/* Greeting + Moon */}
      <section className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-gold-300/60">
            {greeting}
          </p>
          <h1 className="mt-1 font-serif text-2xl leading-tight text-gold-200">
            {user?.display_name ?? user?.first_name ?? '✦'}
          </h1>
        </div>
        <div className="float-soft">
          <MoonPhase size={56} />
        </div>
      </section>

      <div className="divider-mystic mb-4">
        <span className="text-base">✦</span>
      </div>

      {/* Lunar event banner — appears only on new/full moon days */}
      {reading?.lunar_event && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4"
        >
          <Link
            to="/rituals"
            className="block rounded-xl border border-gold-500/50 bg-gold-500/10
                       px-4 py-2.5 text-center transition hover:bg-gold-500/20"
          >
            <span className="font-serif text-sm text-gold-200">
              {reading.lunar_event === 'full_moon'
                ? t('lunar.banner_full_today')
                : t('lunar.banner_new_today')}
            </span>
          </Link>
        </motion.div>
      )}

      {/* Streak indicator */}
      <div className="mb-6">
        <StreakChip state={streak} />
      </div>

      {/* Today's wisdom — atmospheric quote rotating daily */}
      {reading?.wisdom && (
        <motion.figure
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-6 rounded-2xl border border-gold-500/15 bg-night-900/30
                     px-4 py-3 text-center"
        >
          <span className="block text-[10px] uppercase tracking-[0.25em] text-gold-500/60">
            ✦ {t('home.wisdom_today')} ✦
          </span>
          <blockquote className="mt-2 font-serif italic leading-snug text-gold-200">
            «{reading.wisdom.text}»
          </blockquote>
          <figcaption className="mt-1 text-[10px] uppercase tracking-widest text-gold-500/60">
            — {reading.wisdom.author}
          </figcaption>
        </motion.figure>
      )}

      {/* Card of the day */}
      <section className="mb-8">
        <h2 className="mb-1 text-center font-serif text-xl text-gold-300">
          {t('home.card_of_day')}
        </h2>
        {/* Countdown — shown when card is open */}
        {reading && revealed && (
          <p className="mb-4 text-center text-[11px] uppercase tracking-widest text-gold-300/50">
            {t('home.next_in', { time: countdown })}
          </p>
        )}
        {!reading && <div className="mb-4" />}

        {loading && (
          <p className="animate-pulse text-center text-gold-300/70">
            {t('reading.loading')}
          </p>
        )}
        {error && <p className="text-center text-rose-400">{error}</p>}

        {reading && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center"
          >
            <OrnateFrame className="rounded-3xl p-3">
              {revealed ? (
                /* After reveal — just the card, no scratch overlay. */
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <TarotCard card={reading.cards[0]} size={240} />
                </motion.div>
              ) : (
                <ScratchCard
                  // 240 × 321 matches the deck aspect (768×1028 ratio).
                  // Was 240 × 372 — a 2:3 ratio that stretched the
                  // back illustration vertically and squeezed the
                  // central sun-moon emblem inward.
                  width={240}
                  height={321}
                  onReveal={onReveal}
                  label={t('home.scratch_to_reveal')}
                  backgroundImage="/cards/_back.jpeg"
                >
                  <TarotCard card={reading.cards[0]} size={240} />
                </ScratchCard>
              )}
            </OrnateFrame>

            {revealed && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="mt-6 max-w-sm"
              >
                <RichText
                  text={reading.interpretation}
                  className="text-center font-serif text-base italic leading-relaxed text-gold-200"
                />
              </motion.div>
            )}
          </motion.div>
        )}
      </section>

      <div className="divider-mystic mb-5"><span>✦</span></div>

      {/* Free CTAs — prominent because no payment friction */}
      <div className="mb-3 grid grid-cols-1 gap-2">
        <Link
          to="/yes-no"
          className="flex items-center justify-between rounded-xl
                     border border-gold-500/40 bg-gold-500/10 px-4 py-3
                     transition hover:bg-gold-500/20"
        >
          <span className="flex items-center gap-3">
            <span className="text-xl">🃏</span>
            <span>
              <span className="block font-serif text-base text-gold-200">
                {t('home.yes_no')}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-gold-400/80">
                {t('home.yes_no_free')}
              </span>
            </span>
          </span>
          <span className="text-gold-400">→</span>
        </Link>
        <Link
          to="/name-compat"
          className="flex items-center justify-between rounded-xl
                     border border-gold-500/40 bg-gold-500/10 px-4 py-3
                     transition hover:bg-gold-500/20"
        >
          <span className="flex items-center gap-3">
            <span className="text-xl">✦</span>
            <span>
              <span className="block font-serif text-base text-gold-200">
                {t('home.name_compat')}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-gold-400/80">
                {t('home.name_compat_free')}
              </span>
            </span>
          </span>
          <span className="text-gold-400">→</span>
        </Link>
        <Link
          to="/natal-chart"
          className="flex items-center justify-between rounded-xl
                     border border-gold-500/40 bg-gold-500/10 px-4 py-3
                     transition hover:bg-gold-500/20"
        >
          <span className="flex items-center gap-3">
            <img
              src="/natal/tile.jpeg"
              alt=""
              className="h-8 w-8 rounded-md object-cover"
              loading="lazy"
            />
            <span>
              <span className="block font-serif text-base text-gold-200">
                {t('home.natal')}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-gold-400/80">
                {t('home.natal_stars')}
              </span>
            </span>
          </span>
          <span className="text-gold-400">→</span>
        </Link>
        <Link
          to="/synastry"
          className="flex items-center justify-between rounded-xl
                     border border-gold-500/40 bg-gold-500/10 px-4 py-3
                     transition hover:bg-gold-500/20"
        >
          <span className="flex items-center gap-3">
            <span className="text-xl">💞</span>
            <span>
              <span className="block font-serif text-base text-gold-200">
                {t('home.synastry')}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-gold-400/80">
                {t('home.synastry_stars')}
              </span>
            </span>
          </span>
          <span className="text-gold-400">→</span>
        </Link>
        <Link
          to="/angel"
          className="flex items-center justify-between rounded-xl
                     border border-gold-500/40 bg-gold-500/10 px-4 py-3
                     transition hover:bg-gold-500/20"
        >
          <span className="flex items-center gap-3">
            <span className="text-xl">🔮</span>
            <span>
              <span className="block font-serif text-base text-gold-200">
                {t('home.angel')}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-gold-400/80">
                {t('home.angel_free')}
              </span>
            </span>
          </span>
          <span className="text-gold-400">→</span>
        </Link>
      </div>

      {/* Quick actions — each tile is icon-badge + serif title with depth. */}
      <nav className="grid grid-cols-2 gap-3">
        {[
          { to: '/spreads', icon: '✦', label: t('home.extended_spread') },
          { to: '/compat',  icon: '❀', label: t('home.compatibility') },
          { to: '/master',  icon: '◈', label: t('home.master_room') },
          { to: '/rituals', icon: '☾', label: t('home.lunar_link') },
          { to: '/journal', icon: '✎', label: t('home.journal') },
          { to: '/diary',   icon: '☼', label: t('home.diary') },
          { to: '/profile', icon: '⚙', label: t('home.settings') },
        ].map((tile) => (
          <Link key={tile.to} to={tile.to} className="tile-action">
            <span className="tile-action__icon">{tile.icon}</span>
            <span className="tile-action__title">{tile.label}</span>
          </Link>
        ))}
      </nav>

      {/* Reward popup — shown one at a time from the queue */}
      <RewardModal reward={rewardQueue[0] ?? null} onDismiss={dismissReward} />
      {showTour && <WelcomeTour onClose={closeTour} />}
    </main>
  );
}
